import clsx from "clsx";
import { Fragment, type ReactNode, useEffect, useRef, useState } from "react";

import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
} from "common/components/ui/Dialog";
import Switcher from "common/components/ui/Switcher";

type FinishedItems<T> = {
  successful: T[];
  failed: T[];
};

interface BulkActionProps<T extends { id: string }> {
  actionFn: (item: T, abortSignal: AbortSignal) => Promise<unknown>;
  items: T[];
  onClose: () => void;
  renderTitle: (finishedItemsCount: number) => string;
  renderReviewItem: (item: T) => ReactNode;
  isAsync?: boolean;
}

export default function BulkAction<T extends { id: string }>({
  actionFn,
  items,
  onClose,
  renderTitle,
  renderReviewItem,
  isAsync,
}: BulkActionProps<T>) {
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const abortControllerRef = useRef<AbortController>();
  const [finishedItems, setFinishedItems] = useState<FinishedItems<T>>({
    successful: [],
    failed: [],
  });
  const [selectedItemType, setSelectedItemType] =
    useState<keyof FinishedItems<T>>("successful");

  useEffect(() => {
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    async function runSequentially() {
      for (const item of items) {
        try {
          await actionFn(item, abortController.signal);
          setFinishedItems((prev) => ({
            ...prev,
            successful: [...prev.successful, item],
          }));
        } catch (error) {
          if (error !== "cleanup") {
            setFinishedItems((prev) => ({
              ...prev,
              failed: [...prev.failed, item],
            }));
          }
        }
      }
    }

    async function runInParallel() {
      const promises = items.map((item) =>
        actionFn(item, abortController.signal)
          .then(() => {
            setFinishedItems((prev) => ({
              ...prev,
              successful: [...prev.successful, item],
            }));
          })
          .catch((error) => {
            if (error !== "cleanup") {
              setFinishedItems((prev) => ({
                ...prev,
                failed: [...prev.failed, item],
              }));
            }
          }),
      );

      await Promise.all(promises);
    }

    if (isAsync) {
      runSequentially();
    } else {
      runInParallel();
    }

    return () => {
      abortController.abort("cleanup");
    };
  }, [actionFn, items, isAsync]);

  const { failed, successful } = finishedItems;
  const finishedItemsCount = successful.length + failed.length;

  let buttonLabel = "Stop";
  let buttonAction = () => abortControllerRef.current?.abort();

  if (finishedItemsCount === items.length) {
    buttonLabel = failed.length ? "Review" : "Done";
    buttonAction = failed.length ? () => setIsReviewOpen(true) : onClose;
  }

  const switcherOptions = [
    {
      value: "successful",
      label: `Successful ${successful.length}`,
    },
    {
      value: "failed",
      label: `Failed ${failed.length}`,
    },
  ];

  return (
    <>
      <div className="flex">
        <div className="flex flex-col gap-y-0.5">
          <span className="text-sm-bold">
            {renderTitle(finishedItemsCount)}
          </span>
          <span className="text-xs-medium text-black-500">
            {successful.length} successful,{" "}
            <span className={clsx(failed.length && "text-red-500")}>
              {failed.length} failed
            </span>
          </span>
        </div>
        <Button
          className="ml-3 my-auto"
          variant="secondary-black"
          size="sm"
          onClick={buttonAction}
        >
          {buttonLabel}
        </Button>
      </div>
      <Dialog
        open={isReviewOpen}
        onOpenChange={setIsReviewOpen}
        onAfterClose={onClose}
      >
        <DialogContent>
          <DialogHeader
            title="Bulk action review"
            subtitle={
              <>
                Action complete with {successful.length} successful and{" "}
                {failed.length} failed
              </>
            }
          />

          <DialogBody>
            <Switcher
              size="sm"
              options={switcherOptions}
              selectedOption={selectedItemType}
              onOptionSelect={(option) => {
                setSelectedItemType(option as keyof FinishedItems<T>);
              }}
              className="mx-auto w-72 mb-4"
            />

            <div className="flex flex-col overflow-y-auto scrollbar-thin gap-y-3">
              {finishedItems[selectedItemType].map((item) => (
                <Fragment key={item.id}>{renderReviewItem(item)}</Fragment>
              ))}
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  );
}
